import React from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

/**
 * PaymentSkeleton - Layout tipo Company Data con división 45-55
 * Header: Estructura de PageHeader con 3 columnas (logo, título, close)
 * Columna izquierda: Líneas de datos de pago (sin avatar)
 * Columna derecha: 5 barras verticales (métodos de pago)
 */
const PaymentSkeleton: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const skeletonColors = {
    page: theme.palette.mode === 'light' ? '#f6f8fb' : '#10141C',
    block: theme.palette.mode === 'light' ? '#e9edf2' : '#2f363e',
    paper: theme.palette.mode === 'light' ? '#ffffff' : '#1a1f2a',
    shine: theme.palette.mode === 'light' ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.08)'
  };

  const skeletonStyles = `
    .payment-skeleton-page {
      min-height: 100vh;
      background: ${skeletonColors.page};
      display: flex;
      flex-direction: column;
    }

    /* Header - matching PageHeader structure */
    .payment-skeleton-header {
      background: ${skeletonColors.paper};
      border-bottom: 1px solid ${theme.palette.mode === 'light' ? '#e0e0e0' : '#2f363e'};
    }

    .payment-skeleton-header-inner {
      max-width: 1152px;
      margin: 0 auto;
      padding: 16px 24px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .payment-skel-logo-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      align-items: center;
    }

    .payment-skel-logo {
      width: 120px;
      height: 40px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    .payment-skel-header-title {
      flex: 1;
      display: flex;
      justify-content: center;
    }

    .payment-skel-header-title-block {
      width: 280px;
      height: 28px;
      background: ${skeletonColors.block};
      border-radius: 8px;
    }

    .payment-skel-close-container {
      min-width: ${isMobile ? 'auto' : '200px'};
      display: flex;
      justify-content: flex-end;
    }

    .payment-skel-close {
      width: ${isMobile ? '40px' : '36px'};
      height: ${isMobile ? '40px' : '36px'};
      background: ${skeletonColors.block};
      border-radius: 50%;
    }

    .payment-skeleton-content {
      flex: 1;
      display: flex;
      justify-content: center;
      padding: ${isMobile ? '24px 16px' : '75px 24px 24px'};
    }

    .payment-skeleton-card {
      background: ${skeletonColors.paper};
      border-radius: 16px;
      width: 100%;
      max-width: 1150px;
      overflow: hidden;
      box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    }

    /* Two columns layout */
    .payment-skel-two-columns {
      display: flex;
      min-height: 400px;
    }

    .payment-skel-left-column {
      width: 45%;
      padding: 32px 24px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    .payment-skel-right-column {
      width: 55%;
      padding: 32px;
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      gap: 12px;
    }

    /* Info rows - Datos de pago style */
    .payment-skel-info-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 100%;
      padding: 10px 0;
    }

    .payment-skel-label {
      width: 100px;
      height: 16px;
      background: ${skeletonColors.block};
      border-radius: 4px;
    }

    .payment-skel-value {
      width: 80px;
      height: 16px;
      background: ${skeletonColors.block};
      border-radius: 4px;
    }


    .payment-skel-total-label {
      width: 60px;
      height: 18px;
      background: ${skeletonColors.block};
      border-radius: 4px;
    }

    .payment-skel-total-value {
      width: 100px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Left column title - Datos de Pago - más prominente */
    .payment-skel-left-title {
      width: 160px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
      margin-bottom: 32px;
    }

    /* Payment method bars - 5 bars */
    .payment-skel-section-title {
      width: 200px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
      margin-bottom: 12px;
    }

    .payment-skel-method-bar {
      width: 100%;
      height: 56px;
      background: ${skeletonColors.block};
      border-radius: 12px;
    }

    /* Footer */
    .payment-skeleton-footer {
      padding: 24px;
      display: flex;
      justify-content: center;
    }

    .payment-skel-footer-logo {
      width: 100px;
      height: 24px;
      background: ${skeletonColors.block};
      border-radius: 6px;
    }

    /* Shimmer animation */
    .shimmer {
      position: relative;
      overflow: hidden;
    }

    .shimmer::after {
      content: "";
      position: absolute;
      inset: 0;
      transform: translateX(-150%);
      background: linear-gradient(90deg, transparent 0%, ${skeletonColors.shine} 50%, transparent 100%);
      animation: paymentShimmer 1.6s ease-in-out infinite;
    }

    @keyframes paymentShimmer {
      0% { transform: translateX(-150%); }
      100% { transform: translateX(150%); }
    }

    @media (max-width: 960px) {
      .payment-skel-two-columns {
        flex-direction: column;
      }
      .payment-skel-left-column {
        width: 100%;
      }
      .payment-skel-right-column {
        width: 100%;
      }
    }
  `;

  return (
    <div className="payment-skeleton-page">
      <style>{skeletonStyles}</style>
      
      {/* Header - estructura de PageHeader */}
      <div className="payment-skeleton-header">
        <div className="payment-skeleton-header-inner">
          <div className="payment-skel-logo-container">
            <div className="payment-skel-logo shimmer" />
          </div>
          {!isMobile && (
            <div className="payment-skel-header-title">
              <div className="payment-skel-header-title-block shimmer" />
            </div>
          )}
          <div className="payment-skel-close-container">
            <div className="payment-skel-close shimmer" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="payment-skeleton-content">
        <div className="payment-skeleton-card">
          <div className="payment-skel-two-columns">
            {/* Left Column - 45% - Datos de Pago (sin avatar) */}
            <div className="payment-skel-left-column">
              {/* Título "Datos de Pago" - más prominente */}
              <div className="payment-skel-left-title shimmer" />
              
              <div className="payment-skel-info-row">
                <div className="payment-skel-label shimmer" />
                <div className="payment-skel-value shimmer" />
              </div>
              <div className="payment-skel-info-row">
                <div className="payment-skel-label shimmer" />
                <div className="payment-skel-value shimmer" />
              </div>
              <div className="payment-skel-info-row">
                <div className="payment-skel-label shimmer" />
                <div className="payment-skel-value shimmer" />
              </div>
              <div className="payment-skel-info-row">
                <div className="payment-skel-label shimmer" />
                <div className="payment-skel-value shimmer" />
              </div>
              <div className="payment-skel-info-row">
                <div className="payment-skel-total-label shimmer" />
                <div className="payment-skel-total-value shimmer" />
              </div>
            </div>
            
            {/* Right Column - 55% - 5 Payment Method bars */}
            <div className="payment-skel-right-column">
              <div className="payment-skel-section-title shimmer" />
              <div className="payment-skel-method-bar shimmer" />
              <div className="payment-skel-method-bar shimmer" />
              <div className="payment-skel-method-bar shimmer" />
              <div className="payment-skel-method-bar shimmer" />
              <div className="payment-skel-method-bar shimmer" />
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="payment-skeleton-footer">
        <div className="payment-skel-footer-logo shimmer" />
      </div>
    </div>
  );
};

export default PaymentSkeleton;
